import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ArrowUpRight,
  CheckCircle2,
  Clock3,
  MapPinned,
  MessageSquareReply,
  ThumbsUp,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";

dayjs.extend(relativeTime);

const categoryIcons = {
  "Water Supply": "WS",
  Electricity: "EL",
  Road: "RD",
  Education: "ED",
  Health: "HL",
  Agriculture: "AG",
  Other: "OT",
};

const statusStyles = {
  Resolved: "bg-emerald-100 text-emerald-700",
  "Under Review": "bg-amber-100 text-amber-700",
  Open: "bg-rose-100 text-rose-700",
  Flagged: "bg-slate-200 text-slate-700",
};

const IssueCard = ({
  issue,
  variant = "default",
  onRespond,
  onResolve,
  onViewDetails,
  isResolving = false,
}) => {
  const {
    _id,
    title,
    description,
    state,
    district,
    constituency,
    category,
    status,
    createdAt,
    raisedBy,
    upvotesCount,
  } = issue;

  const navigate = useNavigate();
  const icon = categoryIcons[category] || "IS";
  const issueStatus = status || "Open";
  const isCommandCenter = variant === "candidate-dashboard";

  const handleView = () => {
    if (onViewDetails) {
      onViewDetails(issue);
      return;
    }

    navigate(`/issues/${_id}`);
  };

  return (
    <div
      onClick={!isCommandCenter ? handleView : undefined}
      className={`rounded-[1.75rem] border p-5 transition ${
        isCommandCenter
          ? "border-slate-200 bg-white shadow-sm hover:-translate-y-0.5 hover:shadow-lg"
          : "cursor-pointer border-gray-200 bg-white shadow-md hover:scale-[1.01] hover:shadow-lg"
      }`}
    >
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-slate-900/90 px-3 py-1 text-xs font-semibold text-white">
              {icon} {category || "Issue"}
            </span>
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                statusStyles[issueStatus] || "bg-slate-100 text-slate-700"
              }`}
            >
              {issueStatus}
            </span>
          </div>
          <h3 className="mt-3 text-lg font-bold text-slate-900">{title}</h3>
          <p className="mt-2 line-clamp-3 text-sm leading-6 text-slate-600">
            {description}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3 text-sm text-slate-600 sm:min-w-[220px]">
          <div className="rounded-2xl bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-slate-400">
              <MapPinned className="h-3.5 w-3.5" />
              Location
            </div>
            <p className="mt-1 font-medium text-slate-700">
              {constituency || district || state || "N/A"}
            </p>
          </div>
          <div className="rounded-2xl bg-slate-50 px-3 py-2">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-slate-400">
              <Clock3 className="h-3.5 w-3.5" />
              Received
            </div>
            <p className="mt-1 font-medium text-slate-700">
              {createdAt ? dayjs(createdAt).fromNow() : "Just now"}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm text-slate-500">
        <span><strong>State:</strong> {state || "N/A"}</span>
        <span><strong>District:</strong> {district || "N/A"}</span>
        <span><strong>Raised By:</strong> {raisedBy?.name || raisedBy || "Anonymous"}</span>
        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-slate-600">
          <ThumbsUp className="h-3.5 w-3.5" />
          {upvotesCount || 0} upvotes
        </span>
      </div>

      {isCommandCenter ? (
        <div className="mt-5 flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            onClick={() => onRespond?.(issue)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
          >
            <MessageSquareReply className="h-4 w-4" />
            Respond
          </button>
          <button
            type="button"
            disabled={isResolving || issueStatus === "Resolved"}
            onClick={() => onResolve?.(issue)}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <CheckCircle2 className="h-4 w-4" />
            {isResolving ? "Resolving..." : "Mark Resolved"}
          </button>
          <button
            type="button"
            onClick={handleView}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            <ArrowUpRight className="h-4 w-4" />
            View Details
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default IssueCard;
